import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { enablePush, pushPermission, pushSupported, pushErrorText } from "@/lib/push";
import { detectLocation, geoErrorText, geoLabel, getGeo } from "@/lib/geo";
import { MapPin, BellRing, Check, Loader2, ShieldCheck } from "lucide-react";

const askedKey = (uid) => `homiie_perms_session_${uid}`;
const BLOCKED_NOTE = "Blocked earlier — open site settings (lock icon by the address bar) → allow it, then tap Allow again.";

async function permState(name) {
    try { return (await navigator.permissions.query({ name })).state; } catch { return "prompt"; }
}

function PermRow({ icon: Icon, title, desc, state, err, note, onAllow, testid }) {
    return (
        <div className="rounded-2xl border border-navy/10 p-4" data-testid={`${testid}-row`}>
            <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal/10 text-teal"><Icon size={19} /></span>
                <div className="flex-1">
                    <p className="font-heading text-sm font-bold text-navy">{title}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-navy/55">{desc}</p>
                    {err && <p className="mt-1.5 text-[11px] font-semibold text-rose-600" data-testid={`${testid}-error`}>{err}</p>}
                    {note && <p className="mt-1.5 text-[11px] font-semibold text-amber-700" data-testid={`${testid}-note`}>{note}</p>}
                </div>
                {state === "done" ? (
                    <span data-testid={`${testid}-done`} className="flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700"><Check size={13} /> On</span>
                ) : (
                    <button data-testid={`${testid}-allow-btn`} onClick={onAllow} disabled={state === "busy"} className="flex items-center gap-1 rounded-full bg-navy px-3.5 py-1.5 text-xs font-bold text-white transition-transform active:scale-95 disabled:opacity-60">
                        {state === "busy" && <Loader2 size={12} className="animate-spin" />} Allow
                    </button>
                )}
            </div>
        </div>
    );
}

// One-time onboarding for location + background notifications; silently re-links push for returning users.
export function PermissionsPrompt() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [loc, setLoc] = useState({ state: getGeo() ? "done" : "idle", err: null, note: null });
    const [push, setPush] = useState({ state: pushPermission() === "granted" ? "done" : "idle", err: null });
    const needsLocation = user && user.role !== "admin";

    useEffect(() => {
        if (!user?.id) { setOpen(false); return; }
        if (pushPermission() === "granted") { enablePush({ ask: false }).catch(() => {}); }
        let t;
        (async () => {
            const geoState = needsLocation ? await permState("geolocation") : "granted";
            const pushState = pushSupported() ? pushPermission() : "granted";
            if (geoState === "granted") setLoc((l) => ({ ...l, state: "done" }));
            if (geoState === "denied") setLoc((l) => ({ ...l, note: BLOCKED_NOTE }));
            if (pushState === "denied") setPush((p) => ({ ...p, err: BLOCKED_NOTE }));
            // Everything allowed → never show again; otherwise ask again once per app session.
            if (geoState === "granted" && pushState === "granted") return;
            if (sessionStorage.getItem(askedKey(user.id))) return;
            t = setTimeout(() => { if (!window.location.pathname.startsWith("/join-pro")) setOpen(true); }, 1500);
        })();
        return () => { clearTimeout(t); };
    }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!open || !user) return null;

    const close = () => { sessionStorage.setItem(askedKey(user.id), "1"); setOpen(false); };
    const allowLocation = async () => {
        setLoc({ state: "busy", err: null, note: null });
        try {
            const g = await detectLocation();
            setLoc({ state: "done", err: null, note: g.services_available ? null : `${geoLabel(g)}: Services coming soon, Care available` });
        } catch (e) { setLoc({ state: "idle", err: geoErrorText(e), note: null }); }
    };
    const allowPush = async () => {
        setPush({ state: "busy", err: null });
        try { await enablePush(); setPush({ state: "done", err: null }); }
        catch (e) { setPush({ state: "idle", err: pushErrorText(e) }); }
    };

    return (
        <div className="fixed inset-0 z-[90] flex items-end justify-center bg-navy/50 backdrop-blur-sm print:hidden" data-testid="permissions-prompt">
            <div className="w-full max-w-md animate-fade-up rounded-t-3xl bg-white p-6 pb-8">
                <div className="mb-1 flex items-center gap-2"><ShieldCheck size={18} className="text-teal" /><h3 className="font-heading text-lg font-extrabold text-navy">Set up HOMIIE</h3></div>
                <p className="mb-4 text-sm text-navy/55">Allow these so we can show what's available near you and keep you updated.</p>
                <div className="space-y-2.5">
                    {needsLocation && (
                        <PermRow icon={MapPin} testid="perm-location" title="Location" state={loc.state} err={loc.err} note={loc.note} onAllow={allowLocation}
                            desc={user.role === "professional" ? "Used to verify on-site check-ins and show nearby jobs." : "Shows Services available in your area. Care is available across Jodhpur."} />
                    )}
                    <PermRow icon={BellRing} testid="perm-push" title="Notifications" state={push.state} err={push.err} onAllow={allowPush}
                        desc={pushSupported() || push.state === "done" ? "Booking, visit and job updates, even when the app is closed." : pushErrorText(new Error("unsupported"))} />
                </div>
                <button data-testid="permissions-done-btn" onClick={close} className="mt-5 w-full rounded-full bg-teal py-3 text-sm font-bold text-white transition-transform active:scale-[0.98]">
                    {loc.state === "done" || push.state === "done" ? "Continue" : "Maybe later"}
                </button>
            </div>
        </div>
    );
}
